import { useMemo, type ReactNode } from "react";
import clsx from "clsx";
import { formatCurrency } from "@/lib/money";
import type { ArticleRevenue, MonthlyCategoryRevenue } from "@/types/camping";

export const MONTH_LABELS = [
  "Jan",
  "Feb",
  "Mär",
  "Apr",
  "Mai",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Okt",
  "Nov",
  "Dez",
] as const;

export function csvEscape(value: string): string {
  if (/[;"\n\r]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

export function downloadCsv(filename: string, rows: string[][]) {
  const bom = "\uFEFF";
  const body = rows.map((row) => row.map(csvEscape).join(";")).join("\n");
  const blob = new Blob([bom + body], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function StatCard({
  label,
  value,
  hint,
  delta,
  valueClassName,
}: {
  label: string;
  value: string;
  hint?: string;
  delta?: ReactNode;
  valueClassName?: string;
}) {
  return (
    <article className="rounded-xl border border-separator bg-surface p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm text-label-secondary">{label}</p>
        {delta}
      </div>
      <p className={clsx("mt-2 text-2xl font-semibold tabular-nums text-label", valueClassName)}>{value}</p>
      {hint ? <p className="mt-2 text-xs leading-relaxed text-label-tertiary">{hint}</p> : null}
    </article>
  );
}

export function deltaPercent(current: number, prior: number): number | null {
  if (prior === 0) return current === 0 ? 0 : null;
  return ((current - prior) / prior) * 100;
}

export function formatSignedPercent(value: number): string {
  const sign = value > 0 ? "+" : "";
  return `${sign}${value.toLocaleString("de-DE", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })} %`;
}

export function DeltaBadge({ current, prior }: { current: number; prior: number }) {
  const delta = deltaPercent(current, prior);
  if (delta == null) {
    return (
      <span className="inline-flex shrink-0 items-center rounded-full bg-fill-tertiary px-2 py-0.5 text-xs font-semibold text-label-secondary">
        Vorjahr 0
      </span>
    );
  }
  const up = delta > 0.05;
  const down = delta < -0.05;
  return (
    <span
      className={clsx(
        "inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums",
        up && "bg-status-green/15 text-status-green",
        down && "bg-status-red/15 text-status-red",
        !up && !down && "bg-fill-tertiary text-label-secondary",
      )}
    >
      <span aria-hidden>{up ? "↑" : down ? "↓" : "→"}</span>
      {formatSignedPercent(delta)}
    </span>
  );
}

export function PointDeltaBadge({
  current,
  prior,
}: {
  current: number | null;
  prior: number | null;
}) {
  if (current == null || prior == null || Number.isNaN(current) || Number.isNaN(prior)) return null;
  const delta = current - prior;
  const up = delta > 0.05;
  const down = delta < -0.05;
  const sign = delta > 0 ? "+" : "";
  const label = `${sign}${delta.toLocaleString("de-DE", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })} PP`;
  return (
    <span
      className={clsx(
        "inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums",
        up && "bg-status-green/15 text-status-green",
        down && "bg-status-red/15 text-status-red",
        !up && !down && "bg-fill-tertiary text-label-secondary",
      )}
    >
      <span aria-hidden>{up ? "↑" : down ? "↓" : "→"}</span>
      {label}
    </span>
  );
}

export function DualMeter({
  current,
  prior,
  currentCaption,
  priorCaption,
}: {
  current: number;
  prior: number;
  currentCaption: string;
  priorCaption: string;
}) {
  const max = Math.max(current, prior, 0.0001);
  const currentWidth = current <= 0 ? 0 : Math.max(3, (current / max) * 100);
  const priorWidth = prior <= 0 ? 0 : Math.max(3, (prior / max) * 100);
  return (
    <div className="mt-4 space-y-2.5">
      <MeterRow width={currentWidth} caption={currentCaption} tone="current" />
      <MeterRow width={priorWidth} caption={priorCaption} tone="prior" />
    </div>
  );
}

function MeterRow({
  width,
  caption,
  tone,
}: {
  width: number;
  caption: string;
  tone: "current" | "prior";
}) {
  return (
    <div>
      <div className="h-1.5 overflow-hidden rounded-full bg-fill-tertiary">
        <div
          className={clsx(
            "h-full rounded-full",
            tone === "current" ? "bg-accent" : "bg-label-tertiary/50",
          )}
          style={{ width: `${width}%` }}
        />
      </div>
      <p className="mt-1 text-[11px] text-label-tertiary">{caption}</p>
    </div>
  );
}

export function LogicNote({ title = "So wird gerechnet", children }: { title?: string; children: ReactNode }) {
  return (
    <details className="rounded-xl border border-separator bg-fill-quaternary px-4 py-3 text-sm text-label-secondary">
      <summary className="cursor-pointer font-medium text-label">{title}</summary>
      <div className="mt-2 space-y-2 leading-relaxed">{children}</div>
    </details>
  );
}

export function formatPercent(value: string | null): string {
  if (value == null || value === "") return "—";
  const n = Number.parseFloat(value);
  if (Number.isNaN(n)) return "—";
  return `${n.toLocaleString("de-DE", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %`;
}

export function percentChange(current: number, prior: number): string | null {
  if (prior === 0) return null;
  const change = ((current - prior) / prior) * 100;
  const sign = change >= 0 ? "+" : "";
  return `${sign}${change.toLocaleString("de-DE", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %`;
}

export function CategoryBarChart({
  items,
  currency,
  selectedCategoryId,
  onSelectCategory,
}: {
  items: MonthlyCategoryRevenue[];
  currency: string;
  selectedCategoryId?: string | null;
  onSelectCategory?: (categoryId: string | null) => void;
}) {
  const max = useMemo(() => {
    const values = items.map((i) => Number.parseFloat(i.revenue_total) || 0);
    return Math.max(...values, 1);
  }, [items]);

  if (items.length === 0) {
    return <p className="text-sm text-label-secondary">Keine Umsätze im Zeitraum.</p>;
  }

  const interactive = onSelectCategory != null;

  return (
    <div className="space-y-3" role="img" aria-label="Umsatz nach Artikelkategorie">
      {items.map((item) => {
        const amount = Number.parseFloat(item.revenue_total) || 0;
        const width = Math.max(2, (amount / max) * 100);
        const key = item.category_id ?? "uncategorized";
        const isSelected = selectedCategoryId != null && selectedCategoryId === key;
        const row = (
          <>
            <div className="mb-1 flex justify-between gap-2 text-sm">
              <span className={isSelected ? "font-medium text-accent" : "text-label"}>
                {item.category_name}
              </span>
              <span className="shrink-0 tabular-nums text-label-secondary">
                {formatCurrency(item.revenue_total, currency)}
              </span>
            </div>
            <div className="h-3 overflow-hidden rounded-full bg-surface-secondary">
              <div
                className={`h-full rounded-full transition-[width] ${
                  isSelected ? "bg-accent" : "bg-accent/80"
                }`}
                style={{ width: `${width}%` }}
              />
            </div>
          </>
        );
        if (!interactive) {
          return <div key={key}>{row}</div>;
        }
        return (
          <button
            key={key}
            type="button"
            className="block w-full rounded-lg p-1 text-left hover:bg-surface-secondary/80"
            aria-pressed={isSelected}
            onClick={() => onSelectCategory(item.category_id)}
          >
            {row}
          </button>
        );
      })}
    </div>
  );
}

function formatQuantity(value: string): string {
  const n = Number.parseFloat(value);
  if (Number.isNaN(n)) return value;
  return n.toLocaleString("de-DE", { maximumFractionDigits: 2 });
}

export function ArticleRevenueTable({
  items,
  currency,
  categoryFilterId,
}: {
  items: ArticleRevenue[];
  currency: string;
  categoryFilterId?: string | null;
}) {
  const filtered = useMemo(() => {
    if (categoryFilterId === undefined || categoryFilterId === null) {
      return items;
    }
    if (categoryFilterId === "uncategorized") {
      return items.filter((a) => a.category_id == null);
    }
    return items.filter((a) => a.category_id === categoryFilterId);
  }, [items, categoryFilterId]);

  const sorted = useMemo(
    () =>
      [...filtered].sort(
        (a, b) =>
          (Number.parseFloat(b.revenue_total) || 0) - (Number.parseFloat(a.revenue_total) || 0),
      ),
    [filtered],
  );

  if (sorted.length === 0) {
    return (
      <p className="text-sm text-label-secondary">
        {categoryFilterId != null
          ? "Keine Artikelumsätze in dieser Kategorie."
          : "Keine Artikelumsätze im Zeitraum."}
      </p>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="eh-table w-full min-w-[36rem] text-sm">
        <thead>
          <tr>
            <th className="text-left">Art.-Nr.</th>
            <th className="text-left">Artikel</th>
            <th className="text-left">Kategorie</th>
            <th className="text-right">Menge</th>
            <th className="text-right">Umsatz</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((row) => (
            <tr key={`${row.article_id ?? "line"}-${row.article_number}-${row.article_name}`}>
              <td className="tabular-nums text-label-secondary">{row.article_number}</td>
              <td>{row.article_name}</td>
              <td className="text-label-secondary">{row.category_name}</td>
              <td className="text-right tabular-nums">{formatQuantity(row.quantity_total)}</td>
              <td className="text-right tabular-nums">
                {formatCurrency(row.revenue_total, currency)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function articleCsvRows(items: ArticleRevenue[]): string[][] {
  return [
    ["Art.-Nr.", "Artikel", "Kategorie", "Menge", "Umsatz"],
    ...items.map((row) => [
      row.article_number,
      row.article_name,
      row.category_name,
      row.quantity_total,
      row.revenue_total,
    ]),
  ];
}

export function categoryCsvRows(items: MonthlyCategoryRevenue[]): string[][] {
  return [
    ["Artikelkategorie", "Umsatz"],
    ...items.map((row) => [row.category_name, row.revenue_total]),
  ];
}
